import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { saveAs } from 'file-saver'
import { GbFileUploadService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-file-upload-service/gb-file-upload.service'
import { catchError, exhaustMap, filter, finalize, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs'
import { Router } from '@angular/router'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbLoaderService } from '@galabid/shared/utils/loader'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { TranslateService } from '@ngx-translate/core'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { GbPaginatedResponse } from 'libs/gb-dashboard/core/data-access/src'
import { GbAssignPaddleDialogComponent } from 'libs/gb-dashboard/user/feature/src/lib/gb-assign-paddle-dialog/gb-assign-paddle-dialog.component'
import { GbChangeUserPasswordComponent } from 'libs/gb-dashboard/user/feature/src/lib/gb-change-user-password/gb-change-user-password.component'
import { GbInviteUserDialogComponent } from 'libs/gb-dashboard/user/feature/src/lib/gb-invite-user-dialog/gb-invite-user-dialog.component'
import { GbMessageUserComponent } from 'libs/gb-dashboard/user/feature/src/lib/gb-message-user/gb-message-user.component'
import { GbUserEditComponent } from 'libs/gb-dashboard/user/feature/src/lib/gb-user-edit/gb-user-edit.component'
import * as ShareProfileActions from 'libs/shared/models/profile/profile.actions'
import { PreferredContactEnum } from 'libs/shared/models/profile/profile.models'
import { ProfileFilters, ProfilesEntity } from './profiles.models'
import { UsersApiService } from './users-api.service'
import * as UsersActions from './users.actions'
import { UsersFacade } from './users.facade'
import { USERS_LOADER } from './users.models'
import {
  FileUploadConfig,
  GbFileUploadDialogComponent,
} from 'libs/gb-dashboard/shared/feature/src/lib/gb-file-upload-dialog/gb-file-upload-dialog.component'

@Injectable()
export class UsersEffects {
  private actions$ = inject(Actions)

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.loadUsers),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.usersFacade.allUsers$),
      switchMap(([action, urlName, allUsers]) => {
        let offset = allUsers.length || 0
        action.shouldReset ? (offset = 0) : (offset = offset)

        const filters: ProfileFilters = {
          ordering: action.ordering,
          search: action.searchTerm,
          invite_state: action.inviteState,
        }

        return this.usersApiService.getProfiles(urlName, offset, filters).pipe(
          map((response: GbPaginatedResponse<ProfilesEntity>) => {
            const props = {
              count: response.count,
              users: response.results,
            }
            if (!action.shouldReset) {
              return UsersActions.loadUsersSuccess(props)
            } else {
              return UsersActions.setUsers(props)
            }
          }),
          catchError((error) => {
            return of(UsersActions.loadUsersFailure({ error }))
          }),
        )
      }),
    ),
  )

  syncAndOpenProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.syncAndOpenProfile),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.usersApiService.getProfile(urlName, action.uuid).pipe(
          switchMap((response: ProfilesEntity) => {
            return [
              UsersActions.getProfileSuccess({ profile: response }),
              UsersActions.selectUser({ uuid: response.uuid }),
              UsersActions.openEditUserDialog({ uuid: response.uuid }),
            ]
          }),
          catchError((error) => {
            return of(UsersActions.getProfileFailure({ error }))
          }),
        )
      }),
    )
  })

  clearSelectedUserQueryParams$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.clearSelectedUser, UsersActions.getProfileFailure),
        map(() => {
          this.router.navigate([], {
            queryParams: {
              profile: null,
            },
            queryParamsHandling: 'merge',
          })
        }),
      ),
    { dispatch: false },
  )

  openEditUserDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.openEditUserDialog),
        exhaustMap(() =>
          this.gbDashboardDialogService.getDialogObs(GbUserEditComponent, {
            disableClose: true,
            width: '800',
            mobileFullScreen: true,
          }),
        ),
      ),
    { dispatch: false },
  )

  closeEditUserDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.updateUserSuccess, UsersActions.openChangePasswordDialog),
        tap((action) => {
          if (action.type === UsersActions.updateUserSuccess.type && action.sendInvite) return
          this.gbDashboardDialogService.closeDialog(GbUserEditComponent)
        }),
      ),
    { dispatch: false },
  )

  updateSelectedUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.updateSelectedUser, UsersActions.updateAndSendInvite),
      withLatestFrom(this.usersFacade.selectedUser$),
      filter(([, selectedUser]) => !!selectedUser),
      switchMap(([action, selectedUser]) => {
        const { user, ...restOfSelectedUser } = selectedUser as ProfilesEntity
        const profilePayload: Omit<ProfilesEntity, 'user'> = {
          ...restOfSelectedUser,
          ...action.profile,
        }
        return this.usersApiService.updateProfile(profilePayload).pipe(
          map((response: ProfilesEntity) =>
            UsersActions.updateUserSuccess({
              update: {
                id: response.uuid,
                changes: response,
              },
              sendInvite: action.type === UsersActions.updateAndSendInvite.type,
            }),
          ),
          catchError((error) => {
            return of(UsersActions.updateUserFailure({ error }))
          }),
        )
      }),
    ),
  )

  assignPaddleNumber = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.assignPaddleNumber),
      switchMap((action) => {
        return this.usersApiService.updateProfile(action.user).pipe(
          map((response: ProfilesEntity) =>
            UsersActions.updateUserSuccess({
              update: {
                id: response.uuid,
                changes: response,
              },
            }),
          ),
          catchError((error) => {
            return of(UsersActions.updateUserFailure({ error }))
          }),
        )
      }),
    ),
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          UsersActions.updateUserFailure,
          UsersActions.loadUsersFailure,
          UsersActions.deleteUserFailure,
          UsersActions.createUserFailure,
          UsersActions.changePasswordFailure,
          UsersActions.messageUserFailure,
          UsersActions.sendInviteError,
          UsersActions.inviteUsersEmailFailure,
          UsersActions.inviteUsersPhoneFailure,
          UsersActions.downloadUsersTemplateFailure,
          ShareProfileActions.sendVerificationFailure,
        ),
        tap((action) => {
          const error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          if (error && typeof error == 'string') {
            this.gbNotificationService.error(error)
          } else {
            this.gbNotificationService.error('GENERIC.ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  showUpdateSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.updateUserSuccess),
        tap((action) => {
          if (!action.sendInvite) this.gbNotificationService.success('USERS.EDIT_USER.UPDATE_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  showDeleteSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.deleteUserSuccess),
        tap(() => this.gbNotificationService.success('USERS.DELETE_SUCCESS')),
      ),
    { dispatch: false },
  )

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.changePassword),
      switchMap((action) => {
        return this.usersApiService.changePassword(action.new_password, action.confirm_password, action.user).pipe(
          map(() => UsersActions.changePasswordSuccess()),
          catchError((error) => {
            return of(UsersActions.changePasswordFailure({ error }))
          }),
        )
      }),
    ),
  )

  openChangePasswordDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.openChangePasswordDialog),
        tap((action) => {
          this.gbDashboardDialogService.openDialog(GbChangeUserPasswordComponent, {
            maxWidth: '500px',
            data: {
              user: action.user,
              uuid: action.uuid,
            },
          })
        }),
      ),
    { dispatch: false },
  )

  closeChangePasswordDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.changePasswordSuccess, UsersActions.openEditUserDialog),
        tap(() => this.gbDashboardDialogService.closeDialog(GbChangeUserPasswordComponent)),
      ),
    { dispatch: false },
  )

  showUpdatePasswordSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.changePasswordSuccess),
        tap(() => this.gbNotificationService.success('USERS.CHANGE_PASSWORD.SUCCESS')),
      ),
    { dispatch: false },
  )

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.deleteUser),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'USERS.DELETE_USER.TITLE',
            body: 'USERS.DELETE_USER.BODY',
            confirmButtonText: 'CONFIRM',
            cancelButtonText: 'CANCEL',
          },
          action,
        ),
      ),
      tap(() => this.gbLoaderService.showLoaderDialog(USERS_LOADER.deleteUser.id, USERS_LOADER.deleteUser.message)),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.usersApiService.deleteUser(urlName, action.uuid).pipe(
          map(() => UsersActions.deleteUserSuccess({ uuid: action.uuid })),
          catchError((error) => of(UsersActions.deleteUserFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(USERS_LOADER.deleteUser.id)),
        ),
      ),
    ),
  )

  openMessageUserDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.openMessageUserDialog),
        tap((action) => {
          this.gbDashboardDialogService.openDialog(GbMessageUserComponent, {
            maxWidth: '500px',
            data: {
              profile: action.profile,
            },
          })
        }),
      ),
    { dispatch: false },
  )

  closeMessageUserDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.messageUserSuccess),
        tap(() => this.gbDashboardDialogService.closeDialog(GbMessageUserComponent)),
      ),
    { dispatch: false },
  )

  messageUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.messageUser),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.usersApiService.messageUser(urlName, action.profile, action.message_template).pipe(
          map(() => UsersActions.messageUserSuccess()),
          catchError((error) => of(UsersActions.messageUserFailure({ error }))),
        )
      }),
    ),
  )

  showMessageUserSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.messageUserSuccess),
        tap(() => this.gbNotificationService.success('USERS.MESSAGE.SUCCESS')),
      ),
    { dispatch: false },
  )

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.createUser),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.usersApiService.createProfile(urlName, action.profile).pipe(
          map((response: ProfilesEntity) =>
            UsersActions.createUserSuccess({
              profile: response,
              onSuccess: action.onSuccess,
            }),
          ),
          catchError((error) => of(UsersActions.createUserFailure({ error }))),
        )
      }),
    ),
  )

  showCreateUserSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.createUserSuccess),
        tap(() => this.gbNotificationService.success('USERS.CREATE_USER.SUCCESS')),
      ),
    { dispatch: false },
  )

  invokeCreateUserSuccessCallback$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.createUserSuccess),
        tap((action) => {
          if (action.onSuccess) {
            action.onSuccess(action.profile)
          }
        }),
      ),
    { dispatch: false },
  )

  showSendInviteSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.sendInviteSuccess),
        tap(() => {
          this.gbNotificationService.success('USERS.MESSAGE.SEND_INVITE_SUCCESS')
          this.gbDashboardDialogService.closeDialog(GbUserEditComponent)
        }),
      ),
    { dispatch: false },
  )

  sendInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.updateUserSuccess),
      filter((action) => !!action.sendInvite),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        const profile = action.update.changes as ProfilesEntity
        return this.usersApiService.sendInvite(urlName, profile.uuid).pipe(
          map(() => UsersActions.sendInviteSuccess()),
          catchError((error) => of(UsersActions.sendInviteError({ error: error }))),
        )
      }),
    ),
  )

  inviteUsersEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.inviteUsersEmail),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.usersApiService.inviteUsersEmail(urlName, action.emails).pipe(
          map((response) => UsersActions.inviteUsersEmailSuccess({ users: response })),
          catchError((error) => of(UsersActions.inviteUsersEmailFailure({ error }))),
        )
      }),
    ),
  )
  inviteUsersPhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.inviteUsersPhoneNumber),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.usersApiService.inviteUsersPhone(urlName, action.phone_numbers).pipe(
          map(() => UsersActions.inviteUsersPhoneSuccess()),
          catchError((error) => of(UsersActions.inviteUsersPhoneFailure({ error }))),
        )
      }),
    ),
  )

  showInviteUserSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.inviteUsersEmailSuccess, UsersActions.inviteUsersPhoneSuccess),
        tap(() => {
          this.gbNotificationService.success('USERS.INVITE_DIALOG.SUCCESS')
          this.gbDashboardDialogService.closeDialog(GbInviteUserDialogComponent, true)
        }),
      ),
    { dispatch: false },
  )

  sendVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareProfileActions.sendVerification),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.usersFacade.selectedUser$),
      filter(([action, urlName, selectedUser]) => !!urlName && (!!selectedUser || !!action.profile)),
      switchMap(([action, urlName, selectedUser]) => {
        const email = action.verification_type === PreferredContactEnum.Email ? selectedUser!.email : action.email
        const phone_number = action.phone_number

        return this.usersApiService
          .sendVerification(
            urlName,
            (selectedUser?.uuid || action.profile?.uuid)!,
            action.verification_type,
            phone_number,
            email,
            action.code_only,
          )
          .pipe(
            map(() => ShareProfileActions.sendVerificationSuccess()),
            catchError((error) => of(ShareProfileActions.sendVerificationFailure({ error }))),
          )
      }),
    ),
  )

  showSendVerificationSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ShareProfileActions.sendVerificationSuccess),
        tap(() => this.gbNotificationService.success('SHARED.PREFERRED_CONTACT.SEND_VERIFICATION_SUCCESS')),
      ),
    { dispatch: false },
  )

  openAssignPaddleDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ShareProfileActions.openAssignPaddleDialog),
        exhaustMap((action) =>
          this.gbDashboardDialogService.getDialogObs(GbAssignPaddleDialogComponent, {
            data: action.pledge,
            width: '500px',
          }),
        ),
      ),
    { dispatch: false },
  )

  closeAssignPaddleDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.updateUserSuccess),
        tap(() => {
          this.gbDashboardDialogService.closeDialog(GbAssignPaddleDialogComponent)
        }),
      ),
    { dispatch: false },
  )

  openImportUsersDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.openImportUsersDialog),
        tap(() =>
          this.gbDashboardDialogService.getDialogObs(GbFileUploadDialogComponent, {
            maxWidth: '740px',
            disableClose: true,
            data: <FileUploadConfig>{
              type: 'USERS',
              onUpload: (file) => this.usersFacade.dispatch(UsersActions.importUsers({ file })),
              onDownload: () => this.usersFacade.dispatch(UsersActions.downloadUsersTemplate()),
            },
          }),
        ),
      ),
    { dispatch: false },
  )

  importUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.importUsers),
      tap(() => this.gbLoaderService.showLoaderDialog(USERS_LOADER.importUsers.id, USERS_LOADER.importUsers.message)),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      mergeMap(([action, urlName]) =>
        this.usersApiService.importUsers(urlName, action.file).pipe(
          map((response) => UsersActions.importUsersSuccess({ response })),
          catchError((error) => of(UsersActions.importUsersFailure({ errorMessages: error.error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(USERS_LOADER.importUsers.id)),
        ),
      ),
    ),
  )

  importUsersError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.importUsersFailure),
        tap((action) => this.gbFileUploadService.uploadErrorsSubject$.next(action.errorMessages)),
      ),
    { dispatch: false },
  )

  importUsersSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.importUsersSuccess),
        tap((action) => {
          this.gbNotificationService.success(
            this.translateService.instant('FILE_UPLOAD_DIALOG.USERS.SUCCESS', {
              count: action.response.users_count,
            }),
          )
          this.gbDashboardDialogService.closeDialog(GbFileUploadDialogComponent)
        }),
      ),
    { dispatch: false },
  )

  downloadUsersTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.downloadUsersTemplate),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) => {
        return this.usersApiService.downloadUsersTemplate(urlName).pipe(
          map((response: Blob) => {
            saveAs(response, 'users_template.xlsx')
            return UsersActions.downloadUsersTemplateSuccess()
          }),
          catchError((error) => {
            return of(UsersActions.downloadUsersTemplateFailure({ error: error }))
          }),
        )
      }),
    ),
  )

  downloadUsersTemplateSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.downloadUsersTemplateSuccess),
        tap(() => {
          this.gbNotificationService.success('FILE_UPLOAD_DIALOG.DOWNLOAD_TEMPLATE_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private selectedAuctionFacade: SelectedAuctionFacade,
    private usersApiService: UsersApiService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private usersFacade: UsersFacade,
    private gbNotificationService: GbNotificationService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbLoaderService: GbLoaderService,
    private gbFileUploadService: GbFileUploadService,
    private router: Router,
    private translateService: TranslateService,
  ) {}
}
